import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

type Props = {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  /** Optionaler Style-Override für das Input. */
  inputStyle?: CSSProperties;
  /** Zusätzliche Klasse am Eingabefeld — für App-eigene Feldgrößen. */
  inputClassName?: string;
  /**
   * Wird zusätzlich zu `onChange` gerufen, wenn ein Vorschlag gewählt wurde —
   * mit den Bestandteilen der Adresse. Für Masken mit getrennten Feldern
   * (Straße / PLZ / Ort): ein Tipp füllt alle drei, statt den Nutzer PLZ und
   * Ort hinterhertippen zu lassen. Felder, die Nominatim nicht liefert,
   * kommen als leerer String — dann den alten Wert stehen lassen.
   */
  onPick?: (teile: Adressteile) => void;
  /**
   * Eigene Vorschlagsquelle statt Nominatim direkt aus dem Browser — für Apps,
   * deren Content-Security-Policy fremde Adressen sperrt (Kontor:
   * `connect-src 'self'`). Erwartet `GET <url>?q=…` mit der Antwort
   * `{ vorschlaege: [{ strasse, plz, ort, kurz, voll }] }` in der Reihenfolge,
   * in der sie gezeigt werden sollen.
   */
  vorschlagUrl?: string;
  /** Verlässt der Nutzer das Feld (z. B. um beim Verlassen zu speichern). */
  onBlur?: () => void;
};

export type Adressteile = {
  /** Straße mit Hausnummer, soweit vorhanden. */
  strasse: string;
  plz: string;
  ort: string;
};

type Suggestion = {
  display_name: string;
  /** Kompakte Anzeige — Stadt + Straße, ohne den ganzen Schwanz aus "Sachsen, Deutschland". */
  short: string;
  teile: Adressteile;
};

/**
 * Ort-Eingabefeld mit Autocomplete via OpenStreetMap Nominatim.
 *
 * Der Nutzer tippt einen Ort, nach 300 ms Debounce erscheinen bis zu fünf
 * Vorschläge. Ein Tipp übernimmt den Wert. Wer ohne Auswahl weitertippt,
 * behält den eingetippten Text — das Feld ist ein Hilfsmittel, kein Zwang.
 *
 * Hinweise:
 * - Nominatim verlangt einen aussagekräftigen User-Agent. Browser können
 *   den `User-Agent`-Header zwar nicht überschreiben, aber `Accept-Language`
 *   für lokalisierte Ergebnisse setzen.
 * - Rate-Limit: 1 req/s. Debounce 300 ms + cancel-on-type hält uns drunter.
 * - Vorschläge erst ab drei Zeichen — sonst Lärm.
 *
 * Die Vorschlagsliste faerbt sich ueber Klassen (`m-ort-*`, siehe
 * vimaana-mobile.css), nicht ueber Inline-Styles: die Apps haben zwei
 * verschiedene Paletten (`--m-*` im Planer, `--vg-*` in Kontor), und nur so
 * sieht die Liste in beiden richtig aus statt farblos.
 */
export function MOrtAutocomplete({
  value, onChange, placeholder, autoFocus, inputStyle, inputClassName, onPick, vorschlagUrl, onBlur,
}: Props) {
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    // Abort laufende Anfrage, wenn der Wert sich ändert.
    if (abortRef.current) abortRef.current.abort();

    const q = value.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    const ctl = new AbortController();
    abortRef.current = ctl;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        if (vorschlagUrl) {
          const res = await fetch(`${vorschlagUrl}?q=${encodeURIComponent(q)}`, {
            signal: ctl.signal,
            credentials: "include",
            headers: { "Accept": "application/json" },
          });
          if (!res.ok) throw new Error("vorschlaege status " + res.status);
          const data = await res.json() as { vorschlaege?: Array<{ strasse: string; plz: string; ort: string; kurz: string; voll: string }> };
          setSuggestions((data.vorschlaege || []).map((v) => ({
            display_name: v.voll,
            short: v.kurz,
            teile: { strasse: v.strasse, plz: v.plz, ort: v.ort },
          })));
        } else {
          const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&accept-language=de&q=${encodeURIComponent(q)}`;
          const res = await fetch(url, {
            signal: ctl.signal,
            headers: { "Accept": "application/json" },
          });
          if (!res.ok) throw new Error("nominatim status " + res.status);
          const data: Array<Record<string, unknown>> = await res.json();
          setSuggestions(data.map((d) => toSuggestion(d)));
        }
      } catch (err) {
        if ((err as { name?: string }).name !== "AbortError") {
          setSuggestions([]);
        }
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [value, vorschlagUrl]);

  return (
    <div className="m-ort-wrap">
      <input
        type="text"
        className={`m-ort-input${inputClassName ? " " + inputClassName : ""}`}
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => {
          // Mit Verzögerung schließen, damit ein Tap auf eine Vorschlags-Reihe
          // noch durchklickt, bevor wir den Container ausblenden.
          setTimeout(() => setOpen(false), 150);
          onBlur?.();
        }}
        placeholder={placeholder}
        autoFocus={autoFocus}
        // Sonst legt der Browser seine eigene Adress-Liste über die Vorschläge.
        autoComplete="off"
        style={inputStyle}
      />
      {open && (loading || suggestions.length > 0) ? (
        <div className="m-ort-list">
          {loading ? (
            <div className="m-ort-loading">Suche…</div>
          ) : suggestions.map((s, i) => (
            <button
              key={i}
              type="button"
              className="m-ort-item"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(onPick ? s.teile.strasse || s.short : s.short);
                onPick?.(s.teile);
                setSuggestions([]);
                setOpen(false);
              }}
            >
              <span className="m-ort-item-short">{s.short}</span>
              <span className="m-ort-item-full">{s.display_name}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Macht aus dem Nominatim-Rückgabewert eine kompakte Anzeige.
 * Beispiel display_name: "Bautzner Straße, Striesen-Ost, Dresden, Sachsen, 01307, Deutschland"
 * → short: "Bautzner Straße, Dresden"
 */
function toSuggestion(d: Record<string, unknown>): Suggestion {
  const display = String(d.display_name || "");
  const addr = (d.address as Record<string, unknown> | undefined) ?? {};
  const street = (addr.road as string | undefined) || (addr.pedestrian as string | undefined) || "";
  const city = (addr.city as string | undefined)
    || (addr.town as string | undefined)
    || (addr.village as string | undefined)
    || (addr.municipality as string | undefined)
    || "";
  const short = [street, city].filter(Boolean).join(", ")
    || display.split(",").slice(0, 2).join(",").trim()
    || display;
  const hausnr = (addr.house_number as string | undefined) || "";
  return {
    display_name: display,
    short,
    teile: {
      strasse: [street, hausnr].filter(Boolean).join(" "),
      plz: (addr.postcode as string | undefined) || "",
      ort: city,
    },
  };
}
